import { createAuthClient } from 'better-auth/react'
import { zielDerAnmeldung } from './netcon-cloud'

/**
 * Anmelde-Client. Spricht die Endpunkte unter `/api/auth/*` an – dieselbe
 * Herkunft, deshalb keine URL nötig.
 *
 * Nur Anmelden und Abmelden laufen hierüber (dabei werden Cookies gesetzt).
 * Wer angemeldet ist, kommt aus `trpc.auth.me` – dort ist die Antwort schon
 * unsere Person mit Rolle, nicht der rohe Anmeldenutzer.
 */
export const authClient = createAuthClient()

export async function anmelden(email: string, passwort: string): Promise<void> {
  const { error } = await authClient.signIn.email({ email, password: passwort })
  if (error) {
    // Absichtlich keine Unterscheidung „Konto unbekannt" / „Passwort falsch":
    // das verrät sonst, welche Adressen existieren.
    throw new Error(
      error.status === 401 || error.status === 403
        ? 'E-Mail oder Passwort stimmt nicht.'
        : (error.message ?? 'Anmeldung fehlgeschlagen.'),
    )
  }
}

/** Was der Server nach einer gelungenen Anmeldung zurückgibt. */
export interface NetconAnmeldung {
  /**
   * **Der Umweg über NetCon.Cloud**, wenn der Rückweg zustande kam (17.09.2026,
   * [E-364](../../../../docs/ENTSCHEIDUNGEN.md#e-364)).
   *
   * Der Browser springt dorthin, das Portal meldet an und schickt ihn auf dieselbe Seite
   * zurück – danach ist die Kopfleiste da. Fehlt das Feld, war es eine gewöhnliche Anmeldung;
   * dann passiert nichts Zusätzliches, und das ist der Normalfall für alle ohne
   * NetCon-Zugangsdaten.
   */
  weiter?: string
}

/**
 * **Anmelden mit NetCon-Zugangsdaten** (24.08.2026).
 *
 * Als eigener Aufruf und nicht über `authClient`: der Endpunkt kommt aus einem eigenen
 * Better-Auth-Plugin (`apps/api/src/auth/netcon-plugin.ts`), und den kennt der erzeugte Client
 * nur mit einem passenden Client-Plugin. Ein `fetch` auf denselben Pfad ist ehrlicher als eine
 * zweite Typenschicht für einen einzigen Aufruf – das Cookie setzt ohnehin der Server.
 *
 * ## Warum hier **doch** unterschieden wird
 *
 * Beim Work-Passwort steht oben, warum „Konto unbekannt" und „Passwort falsch" dieselbe Meldung
 * bekommen: sonst verrät die Maske, welche Adressen es gibt. Hier ist das anders – der Server
 * prüft gegen **NetCon**, und die Antwort sagt nichts über den Work-Bestand aus. Was sie sagt,
 * ist für den Menschen davor entscheidend: eine falsche Domain, ein NetCon-Ausfall und ein
 * falsches Passwort verlangen drei verschiedene Reaktionen.
 */
export async function netconAnmelden(email: string, passwort: string): Promise<NetconAnmeldung> {
  const antwort = await fetch('/api/auth/sign-in/netcon', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password: passwort, ziel: zielDerAnmeldung() }),
  })

  if (antwort.ok) {
    const daten = (await antwort.json().catch(() => ({}))) as { weiter?: unknown }
    return typeof daten.weiter === 'string' && daten.weiter !== '' ? { weiter: daten.weiter } : {}
  }

  const text = await antwort.text().catch(() => '')
  let meldung = ''
  try {
    const daten = JSON.parse(text) as { message?: unknown }
    if (typeof daten.message === 'string') meldung = daten.message
  } catch {
    /* Keine JSON-Antwort – dann bleibt es beim Satz unten. */
  }

  /** `429` kommt aus der Ratenbegrenzung und trägt keine eigene Erklärung. */
  if (meldung === '' && antwort.status === 429) {
    meldung = 'Zu viele Versuche. Warte eine Minute.'
  }

  throw new Error(meldung || 'Anmeldung über NetCon fehlgeschlagen.')
}

export async function abmelden(): Promise<void> {
  await authClient.signOut()
}
